import React from "react";
//#region types
import { HoldingRegisterFormTypes } from "models/formTypes";
import { Amount } from "models/utilTypes/currencyType";
import {
  WintUtilsWebApi,
  WintSuperkollAPIModelHoldingRegisters as HoldingRegistersApiModel,
} from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { TableEnums } from "models/enums";
import { HoldingRegistersListViewProps } from "models/propTypes/holdingRegisterPropTypes";
import { TableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { CurrencyValueFormatter, TableAsync, useFilterContext } from "components/common";
import { useNavigate, useLocation } from "react-router-dom";
import useHoldingRegisterCommonFilterProps from "./forms/useCommonFilterProps";
import { useHoldingRegisterService } from "serviceHooks";
//#endregion
import { UrlHelper, UtilsHelper } from "utils/helpers";

const HoldingRegistersList: React.ForwardRefRenderFunction<TableHandles, HoldingRegistersListViewProps> = (
  props,
  ref
) => {
  const holdingRegisterService = useHoldingRegisterService();
  const { exportFileName, exportFileDescription } = props;
  const location = useLocation();
  const navigate = useNavigate();

  const { filter } = useFilterContext<HoldingRegisterFormTypes.HoldingRegisterFilter>();
  const { filterQueryStateHandler } = useHoldingRegisterCommonFilterProps();

  const filterFunc = React.useMemo(
    () => (baseFilter: WintUtilsWebApi.Filter) => holdingRegisterService.filterHoldingRegisters(baseFilter, filter),
    [filter]
  );

  const getQueryStateForAssetsReport = (searchString: string) => {
    if (filter && filterQueryStateHandler) {
      return UrlHelper.getSearchParamsForQuery(searchString, filterQueryStateHandler);
    }
  };

  const onRowClick = (row: HoldingRegistersApiModel.HoldingRegister) => {
    const newLocation = location;
    newLocation.pathname = UrlHelper.formatPath(`/Reports/HoldingRegister/${row.id}`);

    const searchParams = getQueryStateForAssetsReport(location.search);
    if (searchParams !== undefined) {
      newLocation.search = "?" + searchParams.toString();
    }

    navigate(newLocation, {
      state: { background: location },
    });
  };

  return (
    <TableAsync<HoldingRegistersApiModel.HoldingRegister>
      tableRef={ref}
      properties={{
        assetAccount: {
          description: TranslationKeys.HoldingRegister.AssetAccount,
          noWrap: true,
          formatter: (r: HoldingRegistersApiModel.HoldingRegister) =>
            r.assetAccount
              ? `${r.assetAccount}`.concat(r.assetAccountName ? ` - ${r.assetAccountName}` : "")
              : undefined,
        },
        depreciatingAccount: {
          description: TranslationKeys.HoldingRegister.DepreciatingAccount,
          noWrap: true,
          formatter: (r: HoldingRegistersApiModel.HoldingRegister) =>
            r.depreciatingAccount
              ? `${r.depreciatingAccount}`.concat(r.depreciatingAccountName ? ` - ${r.depreciatingAccountName}` : "")
              : undefined,
        },
        numberOfAssets: {
          description: TranslationKeys.HoldingRegister.NumberOfAssets,
          noWrap: true,
          formatter: (r: HoldingRegistersApiModel.HoldingRegister) => UtilsHelper.getCountText(r.numberOfAssets),
          summaryValueFormatter: (summary: HoldingRegistersApiModel.HoldingRegister) => summary.numberOfAssets,
          pdfColumnwidth: 80,
          disableSort: true,
          alignment: "right",
          classification: "number",
        },
        purchaseValue: {
          description: TranslationKeys.HoldingRegister.PurchaseValue,
          formatter: (r: HoldingRegistersApiModel.HoldingRegister) =>
            new Amount(r.purchaseValue?.type, r.purchaseValue?.value),
          summaryValueFormatter: (summary: HoldingRegistersApiModel.HoldingRegister) => (
            <CurrencyValueFormatter cellValue={summary.purchaseValue ? [summary.purchaseValue] : []} />
          ),
          summaryToPrint: (summary: HoldingRegistersApiModel.HoldingRegister) =>
            summary.purchaseValue ? [summary.purchaseValue] : [],
          alignment: "right",
          noWrap: true,
          disableSort: true,
        },
        accumulatedDepreciation: {
          description: TranslationKeys.HoldingRegister.AccumulatedDepreciation,
          formatter: (r: HoldingRegistersApiModel.HoldingRegister) =>
            new Amount(r.accumulatedDepreciation?.type, r.accumulatedDepreciation?.value),
          summaryValueFormatter: (summary: HoldingRegistersApiModel.HoldingRegister) => (
            <CurrencyValueFormatter
              cellValue={summary.accumulatedDepreciation ? [summary.accumulatedDepreciation] : []}
            />
          ),
          summaryToPrint: (summary: HoldingRegistersApiModel.HoldingRegister) =>
            summary.accumulatedDepreciation ? [summary.accumulatedDepreciation] : [],
          alignment: "right",
          noWrap: true,
          disableSort: true,
          classification: "amount",
        },
        expectedBookedValue: {
          description: TranslationKeys.HoldingRegister.BookValue,
          formatter: (r: HoldingRegistersApiModel.HoldingRegister) =>
            new Amount(r.expectedBookedValue?.type, r.expectedBookedValue?.value),
          summaryValueFormatter: (summary: HoldingRegistersApiModel.HoldingRegister) => (
            <CurrencyValueFormatter cellValue={summary.expectedBookedValue ? [summary.expectedBookedValue] : []} />
          ),
          summaryToPrint: (summary: HoldingRegistersApiModel.HoldingRegister) =>
            summary.expectedBookedValue ? [summary.expectedBookedValue] : [],
          alignment: "right",
          noWrap: true,
          disableSort: true,
        },
        actualBookedValue: {
          description: TranslationKeys.HoldingRegister.ActualBookedValue,
          formatter: (r: HoldingRegistersApiModel.HoldingRegister) =>
            new Amount(r.actualBookedValue?.type, r.actualBookedValue?.value),
          summaryValueFormatter: (summary: HoldingRegistersApiModel.HoldingRegister) => (
            <CurrencyValueFormatter cellValue={summary.actualBookedValue ? [summary.actualBookedValue] : []} />
          ),
          summaryToPrint: (summary: HoldingRegistersApiModel.HoldingRegister) =>
            summary.actualBookedValue ? [summary.actualBookedValue] : [],
          alignment: "right",
          noWrap: true,
          disableSort: true,
        },
      }}
      filter={filterFunc}
      pagination={"advanced"}
      maxRowCount={10}
      hidePaginationIfSinglePage
      includeSummary
      tableId={TableEnums.TableType.HoldingRegistersList}
      rowClickHandler={onRowClick}
      exportFileName={exportFileName}
      exportFileDescription={exportFileDescription}
    />
  );
};

export default React.forwardRef(HoldingRegistersList);
